/**
 * N3 · Competência 1 (Números) · task-model H1.1.K01
 *
 *   "Localizar números reais — frações, decimais, negativos — na reta numérica."
 *
 * Três marcadores, cada um com um número escrito, começam na ponta esquerda da reta;
 * o aluno arrasta cada um até o ponto que o representa. A reta é dividida em partes
 * iguais da unidade (meios, terços, quartos, quintos ou décimos), e os marcadores
 * encaixam nessas marcas: posicionar é CONTAR partes, não mirar.
 *
 * CADA MARCADOR NA SUA FAIXA, com uma haste até a reta. Na mesma altura, os nomes se
 * atropelariam quando dois números ficam perto — e é justamente quando ficam perto
 * que a comparação interessa.
 *
 * OS ERROS ALCANÇÁVEIS. Um diagnóstico só vale se o aluno puder de fato cometê-lo: um
 * erro que leva a um ponto fora das marcas da reta nunca aparece. Entram os que caem
 * nelas: −1,5 levado a −0,5 (a parte decimal para o lado errado), o simétrico, uma
 * parte a mais ou a menos (contar marcas em vez de intervalos) e o numerador no lugar
 * do número.
 */

import { formatBR, gcd } from '../../core/num.ts';
import { conta, lista, numeroDe, verbo } from '../../core/pt.ts';
import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type { Vec2 } from '../../geom/vec.ts';
import type { Rect, Shape } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  RETA_CONTAGEM,
  RETA_NEGATIVO_PARTE,
  RETA_NUMERADOR,
  RETA_SINAL,
} from '../../schema/errors.ts';
import type { DragTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

/** Unidades de desenho por unidade da reta. */
export const U = 100;
const FAIXAS = [0.6, 1.1, 1.6];

type Forma = 'fracao' | 'decimal';

export interface Numero {
  /** Numerador sobre `d` (o denominador da divisão da reta). */
  readonly k: number;
  readonly forma: Forma;
}

export interface Params {
  readonly d: number;
  readonly lo: number;
  readonly hi: number;
  readonly numeros: readonly [Numero, Numero, Numero];
}

const valor = (n: Numero, d: number): number => n.k / d;

/** "7/4", "−3/2", "1,25", "−0,4". */
export function textoDe(n: Numero, d: number): string {
  if (n.forma === 'decimal') {
    const v = n.k / d;
    const casas = Number.isInteger(v * 10) ? 1 : 2;
    const s = formatBR(Math.abs(v), casas);
    return v < 0 ? `−${s}` : s;
  }
  const g = gcd(Math.abs(n.k), d) || 1;
  const a = Math.abs(n.k) / g;
  const b = d / g;
  return `${n.k < 0 ? '−' : ''}${a}/${b}`;
}

function sortear(g: Rng): Params | null {
  const d = g.pick([2, 3, 4, 5, 10]);
  // A reta vai sempre além de −1 e além de 1: é o que dá lugar a um negativo "grande"
  // (o do erro da parte decimal) e a um número maior que 1.
  const lo = g.pick([-3, -2]);
  const hi = lo + 5;
  const permite = (k: number): boolean => k % d !== 0 && k > lo * d && k < hi * d;
  const formaPara = (): Forma => (d === 3 ? 'fracao' : g.bool() ? 'fracao' : 'decimal');
  // Um negativo menor que −1, um maior que 1, e um terceiro em qualquer lugar.
  const kNeg = -g.int(d + 1, Math.abs(lo) * d - 1);
  const kGrande = g.int(d + 1, hi * d - 1);
  const kLivre = g.int(lo * d + 1, hi * d - 1);
  const ks = [kNeg, kGrande, kLivre];
  if (!ks.every(permite)) return null;
  if (new Set(ks).size < 3) return null;
  // Longe o bastante uns dos outros: dois marcadores na mesma marca não se distinguem.
  const ord = [...ks].sort((a, b) => a - b);
  if (ord.some((k, i) => i > 0 && k - (ord[i - 1] as number) < 2)) return null;
  const numeros = ks.map((k) => ({ k, forma: formaPara() })) as [Numero, Numero, Numero];
  return { d, lo, hi, numeros: g.shuffle(numeros) as unknown as [Numero, Numero, Numero] };
}

function drawParams(rng: Rng): Params {
  return sampleWhere(rng.fork('reta'), sortear, (p) => p !== null, {
    label: 'numeros',
    maxTries: 300,
  }) as Params;
}

const quadro = (p: Params): Rect => ({
  x: (p.lo - 0.6) * U,
  y: -0.5 * U,
  w: (p.hi - p.lo + 1.2) * U,
  h: 2.45 * U,
});

function cenario(p: Params): Shape[] {
  const out: Shape[] = [
    {
      t: 'seg',
      a: { x: (p.lo - 0.4) * U, y: 0 },
      b: { x: (p.hi + 0.4) * U, y: 0 },
      role: 'given',
      arrow: 'end',
    },
  ];
  for (let k = p.lo * p.d; k <= p.hi * p.d; k++) {
    const x = (k / p.d) * U;
    const inteiro = k % p.d === 0;
    const t = inteiro ? 0.09 * U : 0.045 * U;
    out.push({ t: 'seg', a: { x, y: -t }, b: { x, y: t }, role: inteiro ? 'given' : 'auxiliary' });
    if (inteiro) {
      const n = k / p.d;
      out.push({
        t: 'label',
        at: { x, y: -0.12 * U },
        text: [txt(n < 0 ? `−${-n}` : String(n))],
        anchor: 's',
        emphasis: 'tick',
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Correção
// ---------------------------------------------------------------------------

/** Posições erradas típicas de um número, cada uma com o seu erro. */
function errosDe(n: Numero, d: number): { readonly k: number; readonly error: ErrorCode }[] {
  const out: { k: number; error: ErrorCode }[] = [];
  if (n.k < 0 && Math.abs(n.k) > d) {
    // −1,5 = −(1 + 0,5) levado a −1 + 0,5.
    const inteiro = Math.trunc(n.k / d);
    const resto = Math.abs(n.k) - Math.abs(inteiro) * d;
    out.push({ k: inteiro * d + resto, error: RETA_NEGATIVO_PARTE });
  }
  out.push({ k: -n.k, error: RETA_SINAL });
  out.push({ k: n.k + 1, error: RETA_CONTAGEM }, { k: n.k - 1, error: RETA_CONTAGEM });
  if (n.forma === 'fracao') {
    const g = gcd(Math.abs(n.k), d) || 1;
    out.push({ k: (n.k / g) * d, error: RETA_NUMERADOR });
  }
  return out.filter((e) => e.k !== n.k);
}

function alvoDe(p: Params): DragTarget {
  const naReta = (pos: readonly Vec2[]): number[] => pos.map((q) => Math.round((q.x / U) * p.d));
  const certo = (ks: readonly number[], i: number): boolean => ks[i] === p.numeros[i]?.k;
  const erroEm = (ks: readonly number[], i: number): ErrorCode | null => {
    const n = p.numeros[i] as Numero;
    return errosDe(n, p.d).find((e) => e.k === ks[i])?.error ?? null;
  };
  const txtK = (k: number): string => textoDe({ k, forma: 'fracao' }, p.d);

  return {
    describe: [
      txt(
        'Arraste cada marcador até o ponto da reta numérica que representa o número escrito nele.',
      ),
    ],
    correto: (pos) => {
      const ks = naReta(pos);
      return [0, 1, 2].every((i) => certo(ks, i));
    },
    // Um diagnóstico por tipo de erro, na ordem do mais específico para o mais geral.
    diagnostics: [RETA_NEGATIVO_PARTE, RETA_NUMERADOR, RETA_SINAL, RETA_CONTAGEM].map((codigo) => ({
      error: codigo,
      test: (pos: readonly Vec2[]) => {
        const ks = naReta(pos);
        return [0, 1, 2].some((i) => !certo(ks, i) && erroEm(ks, i) === codigo);
      },
    })),
    partialCredit: p.numeros.map((n, i) => ({
      id: `marcador-${i}`,
      label: `${textoDe(n, p.d)} no lugar certo`,
      weight: 1 / 3,
      test: (pos: readonly Vec2[]) => certo(naReta(pos), i),
    })),
    analyze(pos) {
      const ks = naReta(pos);
      const out: Inline[] = [];
      p.numeros.forEach((n, i) => {
        if (i > 0) out.push(br);
        const nome = textoDe(n, p.d);
        if (certo(ks, i)) {
          out.push(strong(`${nome}: `), txt('no lugar certo.'));
          return;
        }
        const k = ks[i] as number;
        out.push(
          strong(`${nome}: `),
          txt(`você colocou em ${txtK(k)}; o lugar certo é ${txtK(n.k)}.`),
        );
        const e = erroEm(ks, i);
        if (e === RETA_NEGATIVO_PARTE)
          out.push(txt(' A parte depois da vírgula de um negativo também vai para a esquerda.'));
        else if (e === RETA_SINAL) out.push(txt(' O sinal ficou trocado.'));
        else if (e === RETA_CONTAGEM)
          out.push(txt(' Faltou ou sobrou uma parte: conte os intervalos, não as marcas.'));
        else if (e === RETA_NUMERADOR)
          out.push(txt(' Esse é o numerador, e não o valor da fração.'));
      });
      return out;
    },
    reviewShapes(pos) {
      const ks = naReta(pos);
      const out: Shape[] = [];
      p.numeros.forEach((n, i) => {
        const alvo = { x: (n.k / p.d) * U, y: 0 };
        if (certo(ks, i)) out.push({ t: 'circle', c: alvo, r: 0.09 * U, role: 'result' });
        else
          out.push(
            { t: 'circle', c: alvo, r: 0.09 * U, role: 'construction' },
            { t: 'point', p: alvo, role: 'ghost' },
          );
      });
      return out;
    },
    reviewLegend: [
      txt(
        'Anel colorido: marcador no lugar certo. Anel tracejado: onde estava o lugar certo de um marcador.',
      ),
    ],
  };
}

function explicar(n: Numero, d: number): string {
  const v = valor(n, d);
  const nome = textoDe(n, d);
  const partes = Math.abs(n.k);
  const inteiro = Math.trunc(Math.abs(v));
  const resto = partes - inteiro * d;
  const sentido = v < 0 ? 'para a esquerda' : 'para a direita';
  // Com a voz passiva, o verbo concorda com o número: "anda-se 1", "andam-se 3". Saía
  // "andam-se 1 para a esquerda" em 62% das variantes. O caso de resto zero — um
  // inteiro escrito como fração, −10/5 — o sorteio de hoje não produz; se passar a
  // produzir, a frase fica certa em vez de dizer "e mais 0 partes".
  const anda = (k: number): string => verbo('andar.pres.se', numeroDe(k));
  const ponto = v < 0 ? `−${inteiro}` : String(inteiro);
  if (inteiro === 0)
    return `${nome}: a unidade está dividida em ${d} partes; a partir do 0, ${anda(partes)} ${partes} delas ${sentido}.`;
  if (resto === 0)
    return `${nome}: são ${partes} partes de 1/${d}, isto é, exatamente ${conta(inteiro, 'inteiro')} — é o próprio ${ponto} da reta.`;
  return (
    `${nome}: são ${partes} partes de 1/${d}, isto é, ${conta(inteiro, 'inteiro')} e mais ${conta(resto, 'parte')} ` +
    `— a partir do ${ponto}, ${anda(resto)} ${resto} ${sentido}.`
  );
}

export const retaNumerica: ItemTemplate<Params, null> = {
  id: templateId('h1-01-K01-reta-numerica'),
  version: 1,
  taskModel: taskModelCode('H1.1.K01'),
  skills: [skillCode('H1.1')],
  axis: 'I',
  level: 'N3',
  title: 'Localizar números na reta numérica',
  responseModel: 'GPCM',
  difficultySources: ['steps.2'],
  params: drawParams,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.d === 3 || p.d === 10) fontes.push('non-round-numbers');
    if (p.numeros.some((n) => n.forma === 'decimal') && p.numeros.some((n) => n.forma === 'fracao'))
      fontes.push('two-supports');
    return fontes;
  },
  build(p) {
    const nomes = p.numeros.map((n) => textoDe(n, p.d));
    const descricao =
      `Reta numérica de ${p.lo < 0 ? `−${-p.lo}` : p.lo} a ${p.hi}, com os inteiros numerados e cada unidade dividida ` +
      `em ${p.d} partes iguais por marcas menores. Três marcadores — ${nomes.join(', ')} — começam na ponta esquerda, ` +
      'cada um numa faixa acima da reta, e podem ser arrastados ao longo dela.';
    return {
      stem: [
        txt('Na reta numérica abaixo, cada unidade está dividida em '),
        strong(`${p.d} partes iguais`),
        txt('. Os marcadores trazem os números '),
        // Ponto e vírgula entre os números e "e" antes do último: com vírgula,
        // "1,6, −2,6, −3/5" não deixa ver onde um número termina e o outro começa.
        strong(lista(nomes, '; ')),
        txt('.'),
      ],
      comando: [
        txt('Arraste cada marcador até o ponto da reta que representa o número escrito nele.'),
      ],
      stimuli: [],
      interaction: {
        kind: 'drag-points',
        descricao,
        backdrop: cenario(p),
        viewBox: quadro(p),
        pontos: p.numeros.map((n, i) => ({
          nome: textoDe(n, p.d),
          inicial: { x: p.lo * U, y: (FAIXAS[i] as number) * U },
        })),
        ligacoes: [],
        passo: U / p.d,
        unidade: U,
        trava: 'x',
        desenho: 'marcadores',
        base: 0,
        limites: { x: p.lo * U, y: 0, w: (p.hi - p.lo) * U, h: 2 * U },
        alvo: alvoDe(p),
      },
      rationale: [
        strong('Resolução. '),
        ...p.numeros.flatMap((n, i) =>
          i > 0 ? [br, txt(explicar(n, p.d))] : [txt(explicar(n, p.d))],
        ),
      ],
      declared: p.d === 3 || p.d === 10 ? 'dificil' : 'media',
    };
  },
};
